import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Link,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { styled } from "@mui/system";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import Cookies from "js-cookie";
import "./Header.css";

/* =========================================================
   HEADER BUTTON
   ========================================================= */

const CustomButton = styled(Button)(() => ({
  minHeight: "42px",
  padding: "0 18px",

  borderRadius: "10px",

  backgroundColor: "#0b6a2e",
  color: "#ffffff",

  fontFamily: '"Inter", sans-serif',
  fontSize: "13px",
  fontWeight: 700,

  lineHeight: 1.2,

  textTransform: "none",

  boxShadow: "0 8px 20px rgba(11, 106, 46, 0.18)",

  whiteSpace: "nowrap",

  transition:
    "background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",

  "&:hover": {
    backgroundColor: "#075124",
    color: "#ffffff",

    transform: "translateY(-1px)",

    boxShadow: "0 10px 24px rgba(11, 106, 46, 0.24)",
  },

  "&:active": {
    transform: "translateY(0)",
  },
}));

/* =========================================================
   HEADER
   ========================================================= */

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const isMobile = useMediaQuery("(max-width: 760px)");

  const location = useLocation();
  const navigate = useNavigate();

  /* =======================================================
     LOGIN STATE
     ======================================================= */

  useEffect(() => {
    const token = Cookies.get("user_auth_token");

    setIsLoggedIn(!!token);
  }, []);

  /* =======================================================
     DRAWER
     ======================================================= */

  const toggleDrawer = (open) => {
    setDrawerOpen(open);
  };

  /* =======================================================
     ACTIVE LINK
     ======================================================= */

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    return location.pathname === path;
  };

  /* =======================================================
     GET STARTED
     ======================================================= */

  const handleButtonClick = () => {
    navigate(isLoggedIn ? "/user/home" : "/user/login");
  };

  /* =======================================================
     NAVIGATION
     ======================================================= */

  const navigationItems = [
    {
      label: "Home",
      path: "/",
    },
    {
      label: "Service",
      path: "/service",
    },
    {
      label: "Pricing",
      path: "/pricing",
    },
    {
      label: "Demo",
      path: "/demo-report",
    },
    {
      label: "Contact",
      path: "/contact",
    },
  ];

  return (
    <header className="farmology-header">
      <AppBar position="sticky" elevation={0} className="farmology-appbar">
        <Toolbar disableGutters className="farmology-toolbar">
          {/* =================================================
              HEADER INNER
          ================================================== */}

          <div className="farmology-header-inner">
            {/* =================================================
                LOGO — LEFT
            ================================================== */}

            <RouterLink
              to="/"
              className="farmology-logo-link"
              aria-label="Farmology Home"
            >
              <Box
                component="img"
                src="/LOGOS/Farmology-Logo-e1687720251448.png"
                alt="Farmology"
                className="farmology-logo"
              />
            </RouterLink>

            {/* =================================================
                DESKTOP NAVIGATION — CENTER
            ================================================== */}

            {!isMobile && (
              <Box className="farmology-desktop-nav">
                <Box className="farmology-nav-pill">
                  {navigationItems.map((item) => (
                    <Link
                      key={item.path}
                      component={RouterLink}
                      to={item.path}
                      underline="none"
                      className={`farmology-nav-link ${
                        isActive(item.path) ? "farmology-nav-link-active" : ""
                      }`}
                    >
                      <Typography component="span">{item.label}</Typography>
                    </Link>
                  ))}
                </Box>
              </Box>
            )}

            {/* =================================================
                DESKTOP CTA — RIGHT
            ================================================== */}

            {!isMobile && (
              <div className="farmology-header-actions">
                <CustomButton
                  variant="contained"
                  onClick={handleButtonClick}
                  className="farmology-header-button"
                >
                  {isLoggedIn ? "Dashboard" : "Get Started"}

                  <span className="farmology-button-arrow">↗</span>
                </CustomButton>
              </div>
            )}

            {/* =================================================
                MOBILE MENU BUTTON
            ================================================== */}

            {isMobile && (
              <IconButton
                className="farmology-menu-button"
                onClick={() => toggleDrawer(true)}
                aria-label="Open menu"
              >
                <MenuIcon />
              </IconButton>
            )}
          </div>

          {/* =================================================
              MOBILE DRAWER
          ================================================== */}

          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={() => toggleDrawer(false)}
            PaperProps={{
              className: "farmology-mobile-drawer",
            }}
          >
            {/* =================================================
                MOBILE DRAWER HEADER
            ================================================== */}

            <div className="farmology-mobile-header">
              <RouterLink
                to="/"
                className="farmology-mobile-logo-link"
                onClick={() => toggleDrawer(false)}
              >
                <Box
                  component="img"
                  src="/LOGOS/Farmology-Logo-e1687720251448.png"
                  alt="Farmology"
                  className="farmology-mobile-logo"
                />
              </RouterLink>

              <IconButton
                className="farmology-close-button"
                onClick={() => toggleDrawer(false)}
                aria-label="Close menu"
              >
                <CloseIcon />
              </IconButton>
            </div>

            <div className="farmology-mobile-divider" />

            {/* =================================================
                MOBILE NAVIGATION
            ================================================== */}

            <List className="farmology-mobile-nav">
              {navigationItems.map((item) => (
                <ListItem
                  key={item.path}
                  disablePadding
                  className={`farmology-mobile-item ${
                    isActive(item.path) ? "farmology-mobile-item-active" : ""
                  }`}
                >
                  <Link
                    component={RouterLink}
                    to={item.path}
                    underline="none"
                    onClick={() => toggleDrawer(false)}
                    className="farmology-mobile-link"
                  >
                    <ListItemText primary={item.label} />

                    <span className="farmology-mobile-arrow">↗</span>
                  </Link>
                </ListItem>
              ))}

              {/* ===============================================
                  MOBILE CTA
              ================================================ */}

              <ListItem disablePadding className="farmology-mobile-button-item">
                <CustomButton
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    toggleDrawer(false);
                    handleButtonClick();
                  }}
                >
                  {isLoggedIn ? "Dashboard" : "Get Started"}

                  <span className="farmology-button-arrow">↗</span>
                </CustomButton>
              </ListItem>
            </List>

            {/* =================================================
                MOBILE FOOTER
            ================================================== */}

            <div className="farmology-mobile-footer">
              <span>FARMOLOGY</span>

              <div />

              <span>SMART AGRICULTURE</span>
            </div>
          </Drawer>
        </Toolbar>
      </AppBar>
    </header>
  );
};

export default Header;
